import { useEffect, useState } from 'react';
import { Logo, PasswordChange, Spinner } from '@wyocspay/ui';
import { api, getToken, setToken, setUnauthorisedHandler } from './api.js';
import { loadIdentity, type DeviceIdentity } from './device.js';
import { Login } from './Login.js';
import {
  AlertsView,
  AuditView,
  DevicesView,
  MerchantsView,
  OverviewView,
  SystemView,
  TransactionsView,
} from './views.js';
import {
  MerchantDetailView,
  PayoutOversightView,
  RefundsDisputesView,
  ReviewQueueView,
  RevenueView,
  SettingsView,
  UsersView,
} from './oversight.js';
import { ApprovalsView, CreateMerchantView, FeatureRequestsView } from './governance.js';
import { DetailRequestsView } from './DetailRequests.js';
import { AdminPaymentDetail } from './PaymentDetail.js';
import { AdminTicketsView } from './Tickets.js';
import { FeesView } from './Fees.js';
import { FeeBillingView } from './FeeBilling.js';
import {
  EmergencyModeView,
  FeeAnalyticsView,
  FeeCatalogView,
  PlansView,
  TariffCalculatorView,
} from './FeeSystem.js';
import { AdsView } from './Ads.js';
import { AccessView } from './Access.js';
import { CertificatesView } from './Certificates.js';
import { CredentialsView } from './Credentials.js';
import { Dac7View } from './Dac7.js';
import { AuditReportView, RevenueReportView } from './Reports.js';
import { AdminTerminalsView } from './Terminals.js';
import { ApplicationsView } from './Applications.js';
import { SubscriptionsView } from './Subscriptions.js';
import { AdminDocumentsView } from './Documents.js';

type NavEntry = { heading: string } | { key: string; label: string };

const NAV: NavEntry[] = [
  { heading: 'Платформа' },
  { key: 'overview', label: 'Общ преглед' },
  { key: 'queue', label: 'Опашка за преглед' },
  { key: 'requests', label: 'Заявки за функции' },
  { key: 'detail-requests', label: 'Заявки за промяна на данни' },
  { key: 'approvals', label: 'Чака второ одобрение' },
  { key: 'alerts', label: 'Сигнали' },
  { key: 'tickets', label: 'Тикети' },
  /**
   * Two populations, two sections.
   *
   * These sat together under "Профили", which put the platform's own staff and
   * the platform's paying clients in one list. They are not the same thing and
   * are almost never administered for the same reason: a merchant is a business
   * that pays to use WyoCSPay, a member of staff is somebody who runs it. The
   * separation is also what stops the wrong account being frozen by whoever is
   * scanning a mixed table at the time.
   */
  { heading: 'Клиенти (търговци)' },
  { key: 'applications', label: 'Заявки за регистрация' },
  { key: 'subscriptions', label: 'Абонаменти (пакети)' },
  { key: 'merchants', label: 'Търговци' },
  { key: 'documents', label: 'Документи' },
  { key: 'new-merchant', label: 'Създай търговец' },
  { key: 'merchant-users', label: 'Хора на търговците' },
  /**
   * Терминалите стоят при търговците, а не в „Операции“ при „Регистрирани
   * устройства“. Двете страници звучат еднакво и не са едно и също: там са
   * машините, които подписват административни команди, тук са телефоните на
   * чужди фирми, стоящи на техните каси. Който търси „кой апарат приема пари за
   * този бизнес“, гледа под заглавието за бизнесите.
   */
  { key: 'terminals', label: 'Картови терминали' },
  { heading: 'Екип на платформата' },
  { key: 'staff-users', label: 'Служители' },
  { key: 'access', label: 'Роли и права' },
  /**
   * Certificates live here, with the other access pages, rather than under
   * "Операции" where they were first put.
   *
   * The owner could not find them, and the heading is why. "Операции" is where
   * the platform's machinery sits - Stripe keys, settings, the audit log - and a
   * certificate is not machinery. It answers "who may come in", which is the
   * question this section already answers. Somebody looking for how to let a
   * person into the console looks under the heading about the people who run it.
   *
   * "Регистрирани устройства" stays in Операции on purpose. Since certificates
   * replaced device enrolment, that page is no longer about who may enter; it is
   * a registry of what may sign a privileged command, which is machinery.
   */
  { key: 'certificates', label: 'Клиентски сертификати' },
  { heading: 'Пари' },
  { key: 'transactions', label: 'Всички транзакции' },
  { key: 'payouts', label: 'Контрол на изплащанията' },
  { key: 'refunds', label: 'Рефунди и спорове' },
  { key: 'revenue', label: 'Приходи на платформата' },
  { key: 'revenue-report', label: 'Отчет за приходите' },
  { key: 'fee-billing', label: 'Такси за фактуриране' },
  { key: 'fees', label: 'Такси и отговорност' },
  { key: 'fee-analytics', label: 'Анализ на таксите' },
  { key: 'tariff-calculator', label: 'Калкулатор на тарифи' },
  { key: 'fee-catalog', label: 'Каталог с тарифи' },
  { key: 'plans', label: 'Ценови планове' },
  { heading: 'Реклама' },
  { key: 'ads', label: 'Банери' },
  { heading: 'Операции' },
  { key: 'dac7', label: 'Отчитане към ЕС (DAC7)' },
  { key: 'credentials', label: 'Ключове за Stripe' },
  { key: 'settings', label: 'Настройки на платформата' },
  { key: 'emergency-mode', label: 'Авариен режим' },
  { key: 'devices', label: 'Регистрирани устройства' },
  { key: 'audit', label: 'Одитен дневник' },
  { key: 'audit-report', label: 'Отчет от одитния дневник' },
  { key: 'system', label: 'Състояние на системата' },
];

const TITLES: Record<string, [string, string]> = {
  overview: ['Общ преглед на платформата', 'Обем, растеж и всичко, което чака решение'],
  queue: ['Опашка за преглед', 'Търговци, които са блокирани, спрени или чакат Stripe'],
  requests: ['Заявки за функции', 'Какво са поискали търговците и защо'],
  'detail-requests': [
    'Заявки за промяна на данни',
    'Какво искат търговците да поправим по профила им - и какво Stripe държи вместо нас',
  ],
  approvals: ['Чака второ одобрение', 'Рискови действия, задържани докато втори администратор не ги подпише'],
  'new-merchant': ['Създай търговец', 'Профилите съществуват само защото вие сте ги създали - няма самостоятелна регистрация'],
  alerts: ['Сигнали', 'Какво откри рекoнсилиацията и не можа да реши само'],
  tickets: ['Тикети', 'Въпросите на търговците, с прикачени файлове и вътрешни бележки'],
  applications: [
    'Заявки за регистрация',
    'Кой е кандидатствал през wyocspay.com - одобрете, поискайте информация или откажете. Одобрението създава профила и входа на собственика',
  ],
  subscriptions: [
    'Абонаменти (пакети)',
    'Заявки за пакети без такси - системата смята подова цена от трансакциите, ти одобряваш на цена не по-ниска от нея',
  ],
  merchants: ['Търговци', 'Всеки бизнес, който приема плащания през платформата - клиентите на платформата'],
  'merchant-users': [
    'Хора на търговците',
    'Служителите на клиентите ви - кой от коя фирма е и чий достъп може да бъде спрян',
  ],
  terminals: [
    'Картови терминали',
    'Телефоните, които приемат плащания на място, при всички търговци - кой ги е регистрирал, кога са били видени за последно и кой има право да ги има',
  ],
  'staff-users': [
    'Служители на платформата',
    'Хората, които управляват WyoCSPay - без нито един профил на търговец в списъка',
  ],
  access: ['Роли и права', 'Кой какво може, и кой може да подписва сам'],
  transactions: ['Всички транзакции', 'Всяко плащане при всеки търговец'],
  payouts: ['Контрол на изплащанията', 'Средства, напускащи платформата, задържани и неуспешни преводи'],
  refunds: ['Рефунди и спорове', 'Пари, връщани към клиентите'],
  revenue: ['Приходи на платформата', 'Какво изкарва платформата по търговец и по метод'],
  'revenue-report': [
    'Отчет за приходите',
    'Готов за счетоводителя документ - по месеци, търговци и методи, за печат, Excel или CSV',
  ],
  'fee-billing': [
    'Такси за фактуриране',
    'Какво е спечелила платформата от всеки клиент за периода - основата за фактурата, не самата фактура',
  ],
  fees: ['Такси и отговорност', 'Какво се удържа от всяко плащане и кой поема загубите'],
  'fee-analytics': [
    'Анализ на таксите',
    'Оценена срещу реална такса, отклонение и марж на платформата - по метод, канал, регион и валута',
  ],
  'tariff-calculator': [
    'Калкулатор на тарифи',
    'Оценка, сравнение на методи, препоръка и праг на рентабилност - пресметнати от същия механизъм като реалното плащане',
  ],
  'fee-catalog': ['Каталог с тарифи', 'Версиите с правила - активни и чернови, с публикуване и връщане назад'],
  plans: ['Ценови планове', 'Публичните планове и на кой план е даден търговец'],
  ads: ['Банери на страницата за плащане', 'Продадени места под формата за плащане - собствени файлове, без външни мрежи'],
  dac7: ['Отчитане към ЕС (DAC7)', 'Кои търговци попадат в обхвата, какво липсва по тях и какво би съдържала декларацията'],
  credentials: ['Ключове за Stripe', 'Ключовете и адресите за webhooks, въведени оттук вместо от сървъра'],
  settings: ['Настройки на платформата', 'Ценообразуване по подразбиране, контроли и аварийни ключове'],
  certificates: ['Клиентски сертификати', 'Кой има достъп до тази администрация, от коя машина и до кога'],
  devices: ['Регистрирани устройства', 'Какво може да подпише привилегирована команда и какво може да бъде отнето'],
  audit: ['Одитен дневник', 'Всяко административно действие, свързано с хешове и проверено при четене'],
  'audit-report': [
    'Отчет от одитния дневник',
    'Извадка по ден, месец, година и действие, с резултата от проверката на веригата на лицевата страна',
  ],
  system: ['Състояние на системата', 'Обработка на събития, доставка и реконсилиация'],
};

export function App() {
  /**
   * The signing key this browser holds, if it holds one.
   *
   * No longer a condition of reaching the console - a client certificate is
   * what gets somebody in, and a fresh browser on a machine that has one will
   * find nothing here. It is still what signs a privileged control command, so
   * a browser without it can read everything and change nothing until a key is
   * registered for it on the devices page.
   */
  const [identity, setIdentity] = useState<DeviceIdentity | null>(null);
  const [user, setUser] = useState<{ id: string; name: string; role: string } | null>(null);
  const [authed, setAuthed] = useState<boolean | null>(null);
  /**
   * PCI DSS 8.3.5. A staff account is created with a password the creating
   * administrator has seen, so it authenticates but authorises nothing until
   * its owner replaces it. The server enforces that; this shows the screen.
   */
  const [mustChangePassword, setMustChangePassword] = useState(false);
  const [view, setView] = useState('overview');
  const [merchantId, setMerchantId] = useState<string | null>(null);
  /** Pending counts per sidebar section, shown as numbers in the nav. */
  const [navCounts, setNavCounts] = useState<Record<string, number>>({});
  const [paymentId, setPaymentId] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      setIdentity(await loadIdentity());
      if (!getToken()) {
        setAuthed(false);
        return;
      }
      try {
        const me = await api.me();
        setUser(me.user);
        setMustChangePassword(me.session.password_change_required);
        setAuthed(true);
      } catch {
        setAuthed(false);
      }
    })();
  }, []);

  /**
   * The pending counts shown in the sidebar. Refreshed when signed in, on each
   * navigation (so acting on a queue updates its number), and on a slow timer.
   */
  useEffect(() => {
    if (authed !== true) return;
    let live = true;
    const load = () =>
      api
        .navCounts()
        .then((r) => {
          if (live) setNavCounts(r.counts);
        })
        .catch(() => undefined);
    void load();
    const timer = setInterval(load, 60_000);
    return () => {
      live = false;
      clearInterval(timer);
    };
  }, [authed, view]);

  /**
   * Any 401, from any view, returns to the sign-in screen.
   *
   * Sessions are short on purpose, so this fires during normal use whenever
   * someone leaves a tab open. Without it the view that happened to be loading
   * just printed the server's "Authentication required" and stayed there.
   */
  useEffect(() => {
    setUnauthorisedHandler(() => {
      setAuthed(false);
      setUser(null);
      setMerchantId(null);
      setPaymentId(null);
    });
    return () => setUnauthorisedHandler(null);
  }, []);

  if (authed === null) {
    return (
      <div style={{ minHeight: '100vh', display: 'grid', placeItems: 'center' }}>
        <Spinner label="Зареждане…" />
      </div>
    );
  }

  if (!authed || !user) {
    return (
      <Login
        onDone={async () => {
          setIdentity(await loadIdentity());
          const me = await api.me();
          setUser(me.user);
          setMustChangePassword(me.session.password_change_required);
          setAuthed(true);
        }}
      />
    );
  }

  /**
   * In front of the console rather than inside it. Every admin endpoint refuses
   * this session until the password is replaced, so there is no console behind
   * this screen to show.
   */
  if (mustChangePassword) {
    return (
      <PasswordChange
        reason="assigned"
        onSubmit={async (current, next) => {
          await api.changePassword(current, next);
          // Succeeding revoked every session for this account, this one too.
          setToken(null);
          setMustChangePassword(false);
          setAuthed(false);
        }}
      />
    );
  }

  const [title, subtitle] = paymentId
    ? ['Плащане', 'Всеки опит, линк, рефунд и промяна на състоянието, с източника си']
    : merchantId
      ? ['Търговец', 'Състояние в Stripe, ценообразуване, потребители и история']
      : (TITLES[view] ?? ['', '']);
  const signer = { identity, actorId: user.id };

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <header
        style={{
          background: 'var(--wc-blue-header)',
          color: '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 16,
          padding: '13px 20px',
          flexWrap: 'wrap',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Logo height={56} />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, fontSize: 13 }}>
          <span style={{ color: '#dbe9f2' }}>
            {user.name} · {user.role}
          </span>
          <button
            type="button"
            className="wc-btn wc-btn-sm"
            style={{ background: 'transparent', borderColor: 'rgba(255,255,255,0.35)' }}
            onClick={async () => {
              await api.logout().catch(() => undefined);
              setToken(null);
              setAuthed(false);
            }}
          >
            Изход
          </button>
        </div>
      </header>

      <div style={{ flex: 1, display: 'flex' }} className="panel-shell">
        <nav
          aria-label="Раздели"
          className="panel-nav"
          style={{
            width: 216,
            flex: 'none',
            background: 'var(--wc-surface)',
            borderRight: '1px solid var(--wc-border)',
            padding: '14px 0',
          }}
        >
          {NAV.map((entry, i) =>
            'heading' in entry ? (
              <div
                key={`h${i}`}
                style={{
                  padding: '14px 18px 5px',
                  fontSize: 10.5,
                  fontWeight: 700,
                  letterSpacing: '0.1em',
                  textTransform: 'uppercase',
                  color: 'var(--wc-faint)',
                }}
              >
                {entry.heading}
              </div>
            ) : (
              <button
                key={entry.key}
                type="button"
                onClick={() => { setMerchantId(null); setPaymentId(null); setView(entry.key); }}
                aria-current={view === entry.key ? 'page' : undefined}
                style={{
                  width: '100%',
                  padding: '8px 18px',
                  border: 'none',
                  borderLeft: `3px solid ${view === entry.key ? 'var(--wc-blue)' : 'transparent'}`,
                  background: view === entry.key ? 'var(--wc-blue-tint)' : 'transparent',
                  color: view === entry.key ? 'var(--wc-blue-ink)' : 'var(--wc-body)',
                  fontSize: 14,
                  fontWeight: view === entry.key ? 600 : 400,
                  textAlign: 'left',
                  transition: 'background 0.15s ease',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 8,
                }}
              >
                <span>{entry.label}</span>
                {navCounts[entry.key] ? (
                  <span
                    style={{
                      flex: 'none',
                      background: 'var(--wc-blue)',
                      color: '#fff',
                      fontSize: 11,
                      fontWeight: 700,
                      borderRadius: 999,
                      padding: '1px 7px',
                      minWidth: 20,
                      textAlign: 'center',
                    }}
                  >
                    {navCounts[entry.key]}
                  </span>
                ) : null}
              </button>
            ),
          )}
        </nav>

        <main style={{ flex: 1, minWidth: 0, padding: '22px 24px' }}>
          <div style={{ marginBottom: 20 }}>
            <h1 style={{ fontSize: 22 }}>{title}</h1>
            <p style={{ margin: '3px 0 0', color: 'var(--wc-muted)', fontSize: 14 }}>{subtitle}</p>
          </div>

          {paymentId ? (
            <AdminPaymentDetail
              id={paymentId}
              signer={signer}
              onBack={() => setPaymentId(null)}
              onOpenMerchant={(m) => { setPaymentId(null); setMerchantId(m); }}
            />
          )
          : merchantId ? (
            <MerchantDetailView id={merchantId} signer={signer} onBack={() => setMerchantId(null)} />
          )
          : view === 'overview' ? <OverviewView />
          : view === 'queue' ? <ReviewQueueView onOpenMerchant={setMerchantId} />
          : view === 'requests' ? <FeatureRequestsView />
          : view === 'detail-requests' ? <DetailRequestsView />
          : view === 'approvals' ? <ApprovalsView signer={signer} />
          : view === 'new-merchant' ? <CreateMerchantView />
          : view === 'alerts' ? <AlertsView identity={identity} actorId={user.id} />
          : view === 'tickets' ? <AdminTicketsView />
          : view === 'applications' ? <ApplicationsView onOpenMerchant={setMerchantId} />
          : view === 'subscriptions' ? <SubscriptionsView onOpenMerchant={setMerchantId} />
          : view === 'merchants' ? <MerchantsView identity={identity} actorId={user.id} onOpen={setMerchantId} />
          : view === 'documents' ? <AdminDocumentsView />
          : view === 'merchant-users' ? <UsersView signer={signer} scope="merchant" onOpenMerchant={setMerchantId} />
          : view === 'terminals' ? <AdminTerminalsView signer={signer} onOpenMerchant={setMerchantId} />
          : view === 'staff-users' ? <UsersView signer={signer} scope="staff" />
          : view === 'access' ? <AccessView currentUserId={user.id} />
          : view === 'transactions' ? <TransactionsView onOpen={setPaymentId} />
          : view === 'payouts' ? <PayoutOversightView />
          : view === 'refunds' ? <RefundsDisputesView signer={signer} onOpen={setPaymentId} />
          : view === 'revenue' ? <RevenueView />
          : view === 'revenue-report' ? <RevenueReportView />
          : view === 'fee-billing' ? <FeeBillingView onOpenMerchant={setMerchantId} />
          : view === 'fees' ? <FeesView />
          : view === 'fee-analytics' ? <FeeAnalyticsView />
          : view === 'tariff-calculator' ? <TariffCalculatorView />
          : view === 'fee-catalog' ? <FeeCatalogView />
          : view === 'plans' ? <PlansView />
          : view === 'ads' ? <AdsView />
          : view === 'dac7' ? <Dac7View />
          : view === 'credentials' ? <CredentialsView />
          : view === 'settings' ? <SettingsView signer={signer} />
          : view === 'emergency-mode' ? <EmergencyModeView />
          : view === 'certificates' ? <CertificatesView />
          : view === 'devices' ? <DevicesView identity={identity} actorId={user.id} onEnrolled={async () => setIdentity(await loadIdentity())} />
          : view === 'audit' ? <AuditView />
          : view === 'audit-report' ? <AuditReportView />
          : <SystemView />}
        </main>
      </div>

      <style>{`
        @media (max-width: 860px) {
          .panel-shell { flex-direction: column; }
          .panel-nav {
            width: 100% !important;
            border-right: none;
            border-bottom: 1px solid var(--wc-border);
            display: flex;
            overflow-x: auto;
            padding: 0 !important;
          }
          .panel-nav > div { display: none; }
          .panel-nav > button {
            width: auto !important;
            white-space: nowrap;
            border-left: none !important;
            border-bottom: 3px solid transparent;
          }
          .panel-nav > button[aria-current="page"] { border-bottom-color: var(--wc-blue); }
        }
      `}</style>
    </div>
  );
}
